import { makeAutoObservable, reaction, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../types/ServiceStoreTypes";
import { isOperator } from "../../auth/tier";
import { numericUserId } from "../../auth/userId";
import { isFeedbackKind, type TicketRecord } from "./tickets.types";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";
import { bearerHeaders, OBJECT_URL_REVOKE_DELAY_MS, saveBlobInSession } from "shared/sessionDownload";

/** A `documents` row as the API returns it (storage fields stripped). */
export interface TicketAttachment {
  id: string;
  fileName: string;
  originalFileName?: string | null;
  fileSize?: number | null;
  mimeType?: string | null;
  category?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  uploadedBy?: number | null;
  createdAt?: string | null;
  deletedAt?: string | null;
}

/**
 * The API's hard cap (`MAX_UPLOAD_BYTES`, documents.storage.ts). Checked here
 * too so a 30 MB screen recording is refused before 30 MB crosses the wire —
 * the server still decides.
 */
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

/**
 * What the file picker offers — the API's `ALLOWED_MIME_TYPES`, by extension.
 * A browser that reports no type for a file sends it as octet-stream, which the
 * API accepts; this list only shapes the picker.
 */
export const ATTACHMENT_ACCEPT =
  "image/png,image/jpeg,image/gif,image/webp,image/svg+xml,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.md,.zip";

/** The category the upload policy files a ticket attachment under. */
export const TICKET_ATTACHMENT_CATEGORY = "ticket-attachment";

/** How many attachments one read shows. A ticket with more says so. */
export const ATTACHMENTS_LIMIT = 50;

/**
 * Why the attach control is not offered on a ticket, or null when it is.
 *
 * TWO RULES, BOTH THE SERVER'S.
 *
 * Uploading is OPERATOR+ (`documents.content.ts` handleUpload). A viewer is not
 * shown a button the server will refuse.
 *
 * And only on a FEEDBACK KIND. A document is readable by EVERY staff member
 * (`documents` find/get are viewer+), which is wider than the ticket it hangs on
 * — a ticket is read by its requester, assignee, team and operators. The API
 * accepted that trade for the feedback kinds only, which are about a client or a
 * module rather than a person, and for exactly that reason offers the machine
 * attach path (`tickets:file`) on those kinds and no others
 * (documents.upload-policy.ts). A person attaching a file to an `access` or
 * `approval` ticket would publish it to all staff under a ticket most of them
 * cannot open. The API refuses that upload for a person too (since 2026-09-23,
 * `assertAttachableTicket` in documents.content.ts); the screen says why before
 * it is hit rather than hiding the control silently.
 */
export function attachBlockedReason(
  ticket: Pick<TicketRecord, "kind"> | null,
  operator: boolean,
): string | null {
  if (!ticket) return "No ticket is open.";
  if (!isFeedbackKind(String(ticket.kind))) {
    return "Files attach to feedback, feature-request, idea, review and bug tickets only: an attachment can be opened by anyone on staff, which is wider than who can see this ticket.";
  }
  if (!operator) return "Attaching a file needs operator access.";
  return null;
}

/**
 * The attachments of the ticket open in the desk's drawer — the list, the
 * attach control's upload, and the download.
 *
 * A plain class with `makeAutoObservable`, registered beside `ticketDeskStore`
 * (ticketsStoreList.ts) and driven by it: a reaction on the drawer's open
 * ticket loads, clears and re-targets this store, so the drawer component
 * never loads data itself (the web rule: load in stores and route hooks, not
 * in a component effect).
 *
 * THE SAME DELIBERATE DEVIATION AS TicketDeskStore, for a sharper reason:
 * `attachments` holds the find RESPONSE rather than reading
 * `documentsStore.entities`. That ServiceStore is shared with Client 360's
 * documents tab and the meeting upload views, it merges and never evicts, and
 * under its latest-wins policy a find superseded by one of those screens
 * returns its rows to the caller WITHOUT applying them to `entities`. A list
 * derived from the cache would therefore sometimes be empty for a ticket that
 * has files, and would keep showing a file after it was removed. The response
 * is the answer for this ticket; every write re-reads it.
 */
class TicketAttachmentsStore {
  rootStore: RootStore;

  /** The ticket these attachments belong to; null when the drawer is closed. */
  ticketId: number | null = null;
  attachments: TicketAttachment[] = [];
  loading = false;
  /** A read for `ticketId` has ANSWERED — "none" is only said after this. */
  loaded = false;
  error: string | null = null;

  uploading = false;
  uploadError: string | null = null;
  downloadError: string | null = null;

  private loadSeq = 0;
  private uploadSeq = 0;
  /** Bumped by `target`: a download's outcome belongs to the drawer it started in. */
  private targetSeq = 0;

  /** How long a download's object URL outlives the click — see `download`. */
  static readonly REVOKE_DELAY_MS = OBJECT_URL_REVOKE_DELAY_MS;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });

    // Follow the drawer. `ticketDeskStore` is mounted before this store
    // (ticketsStoreList order), and its `selected` is observable; a desk reset
    // on sign-out sets it to null, which empties this store too — so one
    // person's attachments never reach the next person's drawer.
    reaction(
      () => this.selectedTicketId(),
      (id) => this.target(id),
    );
    // And a person change on its own, for a desk that is not mounted: the rows
    // here are one person's reading and must not outlive them.
    reaction(
      () => this.currentUserId(),
      (id, previous) => {
        if (id !== previous) this.target(null);
      },
    );
  }

  private selectedTicketId(): number | null {
    const desk = (this.rootStore as unknown as {
      ticketDeskStore?: { selected?: { id?: unknown } | null };
    }).ticketDeskStore;
    const id = desk?.selected?.id;
    return typeof id === "number" && Number.isFinite(id) ? id : null;
  }

  private selectedTicket(): TicketRecord | null {
    return (
      (this.rootStore as unknown as {
        ticketDeskStore?: { selected?: TicketRecord | null };
      }).ticketDeskStore?.selected ?? null
    );
  }

  private currentUserId(): number | null {
    return numericUserId(
      (this.rootStore as unknown as {
        authStore?: { currentUser?: { id?: unknown } };
      }).authStore?.currentUser?.id,
    );
  }

  private get documentsService(): ServiceStoreType | null {
    return (this.rootStore.documentsStore as ServiceStoreType | null) ?? null;
  }

  // ---- derived ---------------------------------------------------------------

  /** Why the attach control is withheld on the open ticket, or null. */
  get blockedReason(): string | null {
    return attachBlockedReason(this.selectedTicket(), isOperator(this.rootStore));
  }

  get canAttach(): boolean {
    return this.blockedReason === null && this.ticketId !== null;
  }

  /** The list is at the read's cap, so there may be more than it shows. */
  get capped(): boolean {
    return this.attachments.length >= ATTACHMENTS_LIMIT;
  }

  // ---- actions ---------------------------------------------------------------

  /**
   * Point the store at a ticket (or at nothing). A new target is a new
   * generation: a read or upload still in flight for the previous ticket must
   * not land in this one's list.
   */
  target(id: number | null) {
    this.loadSeq += 1;
    this.uploadSeq += 1;
    this.targetSeq += 1;
    this.ticketId = id;
    this.attachments = [];
    this.loaded = false;
    this.loading = false;
    this.error = null;
    this.uploading = false;
    this.uploadError = null;
    this.downloadError = null;
    if (id !== null) void this.load();
  }

  /**
   * Sign-out: point at nothing. `target(null)` is already the whole reset —
   * every counter bumped (so a read, upload or download in flight for the
   * person signing out lands nowhere), the list, the error fields and the
   * upload state emptied. The drawer reaction does the same when the desk's
   * reset closes the ticket; this is for the purge reaching the store directly,
   * and for the case where the desk is not mounted.
   */
  reset() {
    this.target(null);
  }

  clearErrors() {
    this.uploadError = null;
    this.downloadError = null;
  }

  async load() {
    const ticketId = this.ticketId;
    const service = this.documentsService;
    if (ticketId === null || !service) return;
    const seq = ++this.loadSeq;
    runInAction(() => {
      this.loading = true;
    });
    try {
      const res = (await service.find({
        query: {
          entityType: "ticket",
          entityId: String(ticketId),
          $limit: ATTACHMENTS_LIMIT,
          $sort: { createdAt: -1 },
        },
      })) as unknown as
        | { data?: TicketAttachment[] }
        | TicketAttachment[]
        | null
        | undefined;
      if (seq !== this.loadSeq) return;
      runInAction(() => {
        if (!res) {
          // Swallowed by the ServiceStore: a failed read, not "no attachments".
          const err = (service as unknown as {
            errorOnFind?: { message?: unknown } | null;
          }).errorOnFind;
          this.error =
            typeof err?.message === "string" && err.message
              ? err.message
              : "The attachments could not be loaded.";
          this.loaded = true;
          return;
        }
        this.attachments = Array.isArray(res) ? res : (res.data ?? []);
        this.error = null;
        this.loaded = true;
      });
    } catch (err) {
      // Ended by a sign-out: not "loaded", and not a failure — the store was reset.
      if (seq !== this.loadSeq || isSessionEnded(err)) return;
      runInAction(() => {
        this.error =
          (err as Error)?.message || "The attachments could not be loaded.";
        this.loaded = true;
      });
    } finally {
      if (seq === this.loadSeq) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  private token(): string | null {
    return (
      (this.rootStore as unknown as { authStore?: { token?: string | null } })
        .authStore?.token ?? null
    );
  }

  /**
   * Attach a file to the open ticket through the documents service's RAW-BODY
   * endpoint, the same contract Client 360 uses:
   *
   *   POST /documents/upload?entityType=ticket&entityId=<id>&category=ticket-attachment
   *   Content-Type: <file mime>   X-File-Name: <encoded name>   body: the File
   *
   * NOT FormData — the API reads the raw stream. Text files go as
   * application/octet-stream so the app-level body parser does not consume
   * them first (documents.content.ts).
   */
  async upload(file: File): Promise<boolean> {
    const ticketId = this.ticketId;
    if (ticketId === null || this.uploading) return false;
    const blocked = this.blockedReason;
    if (blocked) {
      runInAction(() => {
        this.uploadError = blocked;
      });
      return false;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      runInAction(() => {
        this.uploadError = `${file.name} is larger than the ${MAX_ATTACHMENT_BYTES / (1024 * 1024)} MB attachment limit.`;
      });
      return false;
    }
    if (file.size === 0) {
      runInAction(() => {
        this.uploadError = `${file.name} is empty.`;
      });
      return false;
    }
    const seq = ++this.uploadSeq;
    runInAction(() => {
      this.uploading = true;
      this.uploadError = null;
    });
    // A raw fetch the session guard never sees (shared/sessionDownload.ts).
    const ended = sessionMark(this.rootStore);
    try {
      const { env } = await import("../../config/environment");
      // Before the token is read: after a sign-out it is the next person's.
      if (ended()) return false;
      const query = new URLSearchParams({
        entityType: "ticket",
        entityId: String(ticketId),
        category: TICKET_ATTACHMENT_CATEGORY,
      });
      const contentType =
        file.type && !file.type.startsWith("text/")
          ? file.type
          : "application/octet-stream";
      const headers: Record<string, string> = {
        "Content-Type": contentType,
        "X-File-Name": encodeURIComponent(file.name),
      };
      const token = this.token();
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await fetch(`${env.apiUrl}/documents/upload?${query}`, {
        method: "POST",
        headers,
        body: file,
      });
      if (ended()) return false;
      if (!response.ok) throw new Error(await failureMessage(response, "Upload"));
      // The file IS attached now, whatever the drawer shows next. Re-read when
      // the ticket on screen is THIS ticket — by id, not by upload generation:
      // closing and reopening the same ticket mid-upload starts a new
      // generation whose own read may already have answered without the file,
      // and skipping the refresh then left it missing from the list.
      if (ended()) return false;
      if (this.ticketId === ticketId) await this.load();
      return true;
    } catch (err) {
      if (ended()) return false;
      if (seq === this.uploadSeq) {
        runInAction(() => {
          if (!isSessionEnded(err)) {
            this.uploadError =
              (err as Error)?.message || "The file could not be attached.";
          }
        });
      }
      return false;
    } finally {
      if (seq === this.uploadSeq && !ended()) {
        runInAction(() => {
          this.uploading = false;
        });
      }
    }
  }

  /**
   * Download through the authenticated stream (GET /documents/:id/content with
   * the Bearer header — a plain link would 401), handed to the browser through
   * a transient object URL.
   */
  async download(doc: TicketAttachment): Promise<boolean> {
    // A download started in one ticket's drawer reports there or nowhere: its
    // failure must not surface in the drawer of the ticket opened meanwhile.
    const gen = this.targetSeq;
    runInAction(() => {
      this.downloadError = null;
    });
    // A raw fetch the session guard never sees: a sign-out while the bytes are
    // in flight must not save the previous person's attachment to disk in the
    // next person's session (shared/sessionDownload.ts).
    const ended = sessionMark(this.rootStore);
    try {
      const { env } = await import("../../config/environment");
      // Before the token is read: after a sign-out it is the next person's.
      if (ended()) return false;
      const token = this.token();
      const response = await fetch(
        `${env.apiUrl}/documents/${encodeURIComponent(doc.id)}/content`,
        { headers: bearerHeaders(token) },
      );
      if (ended()) return false;
      if (!response.ok) throw new Error(await failureMessage(response, "Download"));
      const blob = await response.blob();
      // The object URL outlives the click (OBJECT_URL_REVOKE_DELAY_MS): a
      // browser that reads it after this tick would find a revoked one gone.
      return saveBlobInSession(ended, blob, doc.originalFileName || doc.fileName || "attachment");
    } catch (err) {
      if (ended()) return false;
      if (gen === this.targetSeq) {
        runInAction(() => {
          if (!isSessionEnded(err)) {
            this.downloadError =
              (err as Error)?.message || "The attachment could not be downloaded.";
          }
        });
      }
      return false;
    }
  }
}

/**
 * The server's own words for a refusal, when it sent any. A Feathers error's
 * `message` first, then an `error` string (a proxy's or a gateway's shape);
 * any other body — JSON without either, or plain text — is shown as sent
 * rather than dropped for a bare status code.
 */
export async function failureMessage(response: Response, what: string): Promise<string> {
  const text = (await response.text().catch(() => "")).trim();
  try {
    const parsed = JSON.parse(text) as { message?: unknown; error?: unknown } | null;
    if (typeof parsed?.message === "string" && parsed.message) return parsed.message;
    if (typeof parsed?.error === "string" && parsed.error) return parsed.error;
  } catch {
    // Not JSON: the text itself, below.
  }
  if (text && text !== "{}" && text !== "null") return text.slice(0, 300);
  return `${what} failed (${response.status})`;
}

export default TicketAttachmentsStore;
